import React from 'react';
import useScrollAnimation from '@/hooks/useScrollAnimation';

const WhyDifferent = () => {
  const { ref, isVisible } = useScrollAnimation();

  const t = {
    title: 'What Makes Us',
    titleHighlight: 'Different?',
    subtitle: 'Our difference from competitors is not intuitive, it\'s structural.',
    cards: [
      {
        tag: 'NICHE EXPERTISE',
        title: 'Mom & Housewife Experts',
        quote: 'Our expertise is in Mom-Baby, Mom-Child, and Housewife Influencers.',
        description: 'We are tailor-made for brands that want to promote their products with Mom with Baby, Mom with Child, or Housewife Influencers. We have an extensive database and deep knowledge in this niche audience. We don\'t just promote your product, we ensure the right connection with your target audience.',
      },
      {
        tag: 'OPARA SYSTEM',
        title: 'We Speak with Data',
        quote: 'Result projections on the table before the campaign starts.',
        description: 'With our OPARA (Global Influencer Cost-Return Application) supported structure, we can provide views, clicks, and conversion projections before the campaign starts. Our brands can predict the potential return of influencer work before making any investment.',
      },
      {
        tag: 'FULL MANAGEMENT',
        title: 'We Manage the Entire Process End-to-End',
        quote: 'You focus on your strategy, we carry all the burden.',
        description: 'We are aware of how big an operational nightmare multi-influencer campaigns can be for brands. We professionally manage all influencer communications, direction and coordination, legal and financial process management, and end-to-end reporting steps.',
      },
      {
        tag: 'TRANSPARENT MODEL',
        title: 'We Proceed Transparently and Fairly',
        quote: 'Not predatory profit margins, but fair earnings.',
        description: 'We reject the traditional model that puts excessive margins on influencer costs. We work with fair, sustainable, and transparent service fees. Our goal is not to work with a brand on a single project; it is to write a long-term growth story based on mutual trust with that brand.',
      },
    ],
  };

  return (
    <section 
      ref={ref}
      className={`py-12 relative overflow-hidden scroll-animate ${isVisible ? 'visible' : ''}`}
    >
      {/* Animated Background - Same as rest of site */}
      <div className="absolute inset-0 bg-black">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-20 left-10 w-64 h-64 bg-[#FFDD59] rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-[#FFDD59] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 h-80 bg-[#FFDD59] rounded-full blur-3xl animate-pulse" style={{ animationDelay: '2s' }}></div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header */}
        <div className="text-center mb-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t.title} <span className="text-[#FFDD59]">{t.titleHighlight}</span>
          </h2>
          <p className="text-gray-400 text-base md:text-lg">
            {t.subtitle}
          </p>
        </div>

        {/* Cards Grid - Single column on mobile, 2 columns on desktop */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
          {t.cards.map((card, index) => (
            <div 
              key={index}
              className="bg-gray-900 border border-gray-700 rounded-2xl p-5 md:p-8 transition-all duration-300 card-hover-glow hover:border-[#FFDD59]"
            >
              {/* Tag */}
              <span className="inline-block px-3 py-1 bg-[#FFDD59] text-black text-xs font-bold rounded-full mb-3 uppercase tracking-wider">
                {card.tag}
              </span>
              
              {/* Title */}
              <h3 className="text-lg md:text-xl font-bold text-white mb-2">
                {card.title}
              </h3>
              
              {/* Quote */}
              <div className="border-l-4 border-[#FFDD59] pl-3 mb-3">
                <p className="text-[#FFDD59]/75 italic text-sm">
                  {card.quote}
                </p>
              </div>
              
              {/* Description */}
              <p className="text-gray-300 text-sm leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default WhyDifferent;
